import { Application, Container, Graphics, Text, type Ticker } from 'pixi.js';
import { GAME_CONFIG, type GameConfig } from './GameConfig';
import { GameState } from './GameState';
import { SceneManager } from './SceneManager';
import { calculateViewportLayout, type ViewportLayout } from './Viewport';
import type {
  E2EBgmTimelineEntry,
  E2ESceneName,
  E2EPlaythroughResult,
  KonaiE2EApi,
  OrientationOverlayDebugState,
  RuntimeDebugState
} from './E2EDebugApi';
import type { SceneServices } from '../scenes/BaseScene';
import { CaseListScene } from '../scenes/CaseListScene';
import { CreditsScene } from '../scenes/CreditsScene';
import { EndingScene } from '../scenes/EndingScene';
import { EvidenceScene } from '../scenes/EvidenceScene';
import { GalleryScene } from '../scenes/GalleryScene';
import { NovelScene, type NovelSaveProgressResult } from '../scenes/NovelScene';
import { SettingsScene } from '../scenes/SettingsScene';
import { TitleScene } from '../scenes/TitleScene';
import type {
  CaseDefinition,
  GameSettings,
  ScenarioDocument,
  ScenarioLine,
  ScenarioNode
} from './types';
import {
  getCaseSaveTitle,
  getNextCaseDefinition,
  loadScenarioForCase,
  loadScenarioForResume
} from './ScenarioCatalog';
import { AssetLoader } from '../systems/AssetLoader';
import { AudioManager } from '../systems/AudioManager';
import { debugLogger } from '../systems/DebugLogger';
import { InputManager } from '../systems/InputManager';
import { SaveManager } from '../systems/SaveManager';
import { Button } from '../ui/Button';
import {
  DebugOverlay,
  type DebugOverlaySnapshot,
  type DebugOverlayState
} from '../ui/DebugOverlay';
import {
  executeScenarioAction,
  type ScenarioActionExecutionContext
} from '../systems/ScenarioActions';
import { ScenarioRunner } from '../systems/ScenarioRunner';

const DEBUG_OVERLAY_QUERY_PARAM = 'debugOverlay';
const DEBUG_OVERLAY_STORAGE_KEY = 'konai_debug_overlay_v1';
const DEBUG_OVERLAY_TOGGLE_KEY = 'F3';
const DISABLE_ORIENTATION_OVERLAY_QUERY_PARAM = 'disableOrientationOverlay';
const FAST_E2E_AUDIO_SKIP_QUERY_PARAM = 'fastEffects';
const AUDIO_UNLOCK_KEYS = new Set([' ', 'Enter']);
const POINTER_BUTTON_PRIMARY = 0;
const ORIENTATION_DEFAULT_NOTE =
  '切り替わらない場合は、端末の回転ロックを解除して横向きにしてください。';
const ORIENTATION_PENDING_NOTE = '横画面への切り替えを試しています...';
const ORIENTATION_SUCCESS_NOTE =
  '横画面に切り替えました。表示が変わらない場合は端末を横向きにしてください。';
const ORIENTATION_UNSUPPORTED_NOTE =
  'このブラウザではボタンから自動回転できません。端末の回転ロックを解除して横向きにしてください。';
const ORIENTATION_FAILED_NOTE =
  'ブラウザまたは端末設定により自動回転できませんでした。回転ロックを解除して端末を横向きにしてください。';

export class GameApp {
  private readonly pixiApp = new Application();
  private readonly sceneRoot = new Container();
  private readonly layoutDebugLayer = new Container();
  private readonly debugOverlay = new DebugOverlay();
  private readonly orientationOverlay = createOrientationOverlayElement();
  private readonly orientationActionButton =
    this.orientationOverlay.querySelector<HTMLButtonElement>('[data-orientation-action]');
  private readonly orientationStatusText = this.orientationOverlay.querySelector<HTMLElement>(
    '[data-orientation-status]'
  );
  private readonly sceneManager = new SceneManager(this.sceneRoot);
  private readonly assetLoader = new AssetLoader();
  private readonly saveManager: SaveManager;
  private readonly audioManager: AudioManager;
  private readonly gameState: GameState;
  private readonly inputManager: InputManager;
  private viewportLayout: ViewportLayout | null = null;
  private currentSceneName: E2ESceneName = 'title';
  private currentNovelScene: NovelScene | null = null;
  private activeSaveSlotId: number | null = null;
  private debugOverlayEnabled = shouldEnableDebugOverlay();
  private orientationOverlayVisible = false;
  private readonly orientationOverlayDisabled = shouldDisableOrientationOverlay();
  private lastOrientationLockAttemptStatus: OrientationOverlayDebugState['lastLockAttemptStatus'] =
    'idle';

  public constructor(
    private readonly rootElement: HTMLElement,
    private readonly config: GameConfig = GAME_CONFIG
  ) {
    this.saveManager = new SaveManager(config);
    const savedSettings = this.saveManager.loadSettings();
    this.gameState = new GameState(config, {
      settings: savedSettings ?? { reduceMotion: prefersReducedMotion() }
    });
    this.inputManager = new InputManager(rootElement);
    this.audioManager = new AudioManager(
      this.assetLoader,
      this.gameState.getSettings(),
      (settings) => {
        this.gameState.updateSettings(settings);
        this.saveManager.saveSettings(this.gameState.getSettings());
      }
    );
    Button.setDefaultPressSound(() => {
      void this.audioManager.playSe('ui_click');
    });
  }

  public async init(): Promise<void> {
    debugLogger.log('scene', 'init game app', {
      title: this.config.projectFullTitle,
      baseResolution: this.config.baseResolution
    });
    await this.pixiApp.init({
      width: this.config.baseResolution.width,
      height: this.config.baseResolution.height,
      backgroundColor: 0x101010,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2)
    });

    this.pixiApp.canvas.classList.add('game-canvas');
    this.rootElement.replaceChildren(this.pixiApp.canvas, this.orientationOverlay);
    this.pixiApp.stage.addChild(this.sceneRoot);
    this.pixiApp.stage.addChild(this.layoutDebugLayer);
    this.pixiApp.stage.addChild(this.debugOverlay);
    this.debugOverlay.setEnabled(this.debugOverlayEnabled);
    this.applyViewportLayout();
    this.updateDebugOverlay();
    this.showTitleScene();
    this.sceneManager.resize(this.getLayout());
    this.pixiApp.ticker.add(this.handleTick);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);
    window.addEventListener('keydown', this.handleDebugOverlayKeyDown);
    this.orientationActionButton?.addEventListener('click', this.handleOrientationActionClick);
    this.rootElement.addEventListener('pointerup', this.handleAudioUnlockPointerUp, true);
    window.addEventListener('keydown', this.handleAudioUnlockKeyDown, true);
    debugLogger.log('scene', 'game app ready', {
      layout: summarizeLayout(this.getLayout())
    });
  }

  public destroy(): void {
    debugLogger.log('scene', 'destroy game app', { currentSceneName: this.currentSceneName });
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleResize);
    window.removeEventListener('keydown', this.handleDebugOverlayKeyDown);
    this.orientationActionButton?.removeEventListener('click', this.handleOrientationActionClick);
    this.rootElement.removeEventListener('pointerup', this.handleAudioUnlockPointerUp, true);
    window.removeEventListener('keydown', this.handleAudioUnlockKeyDown, true);
    this.pixiApp.ticker.remove(this.handleTick);
    this.currentNovelScene = null;
    this.sceneManager.destroy();
    this.inputManager.destroy();
    this.audioManager.destroy();
    Button.setDefaultPressSound(undefined);
    this.pixiApp.destroy({ removeView: true }, { children: true });
  }

  public getLayout(): ViewportLayout {
    if (!this.viewportLayout) {
      throw new Error('Viewport layout has not been calculated yet.');
    }

    return this.viewportLayout;
  }

  public createE2EApi(): KonaiE2EApi {
    return {
      getSceneName: () => this.currentSceneName,
      getSnapshot: () => this.gameState.getSnapshot(),
      getSettings: () => this.gameState.getSettings(),
      getRuntimeDebugState: () => this.getRuntimeDebugState(),
      getOrientationOverlayDebugState: () => this.getOrientationOverlayDebugState(),
      getAssetDebugState: () => this.assetLoader.getDebugState(),
      getAudioDebugState: () => this.audioManager.getDebugState(),
      getViewportDebugState: () => cloneViewportLayout(this.getLayout()),
      setAudioMuted: (muted) => {
        this.audioManager.mute(muted);
        return this.audioManager.getDebugState();
      },
      playBgmForDebug: (id, options) => this.audioManager.playBgm(id, options),
      stopBgmForDebug: async (fadeMs = 0) => {
        await this.audioManager.stopBgm(fadeMs);
        return this.audioManager.getDebugState();
      },
      playSeForDebug: (id, options) => this.audioManager.playSe(id, options),
      getInputDebugState: () => this.inputManager.getDebugState(),
      getNovelInputDebugState: () => this.currentNovelScene?.getInputDebugState() ?? null,
      getDebugOverlayState: () => this.getDebugOverlayState(),
      getTextLayoutDebugState: () =>
        this.sceneManager.getCurrentScene()?.getTextLayoutDebugState() ?? {
          scene: this.currentSceneName,
          entries: []
        },
      getTitleDebugState: () => {
        const scene = this.sceneManager.getCurrentScene();
        return scene instanceof TitleScene ? scene.getDebugState() : null;
      },
      setDebugOverlayEnabled: (enabled) => this.setDebugOverlayEnabled(enabled),
      listSlots: () => this.saveManager.listSlots(),
      startNewGame: (slotId = 1) => this.e2eStartNewGame(slotId),
      saveCurrentSlot: (slotId, title = `E2E Slot ${slotId}`) =>
        this.saveManager.saveSlot(slotId, this.gameState.getSnapshot(), title),
      loadSlot: (slotId) => this.e2eLoadSlot(slotId),
      deleteSlot: (slotId) => {
        this.saveManager.deleteSlot(slotId);
        return this.saveManager.listSlots();
      },
      updateSettings: (settings) => this.e2eUpdateSettings(settings),
      playCase0ToEnd: (choiceId) => this.e2ePlayCase0ToEnd(choiceId),
      playCaseToEnd: (caseId, choiceId) => this.e2ePlayCaseToEnd(caseId, choiceId),
      goToScenarioNodeForDebug: (nodeId, lineIndex = 0, caseId) =>
        this.e2eGoToScenarioNode(nodeId, lineIndex, caseId),
      setNovelBackgroundForDebug: async (cue) =>
        this.currentNovelScene?.setBackgroundForDebug(cue) ?? null,
      goToTitle: () => {
        this.showTitleScene();
        return this.currentSceneName;
      },
      goToEvidence: () => {
        this.showEvidenceScene(() => this.showTitleScene());
        return this.currentSceneName;
      },
      goToCaseList: () => {
        this.showCaseListScene(() => this.showTitleScene());
        return this.currentSceneName;
      },
      goToGallery: () => {
        this.showGalleryScene(() => this.showTitleScene());
        return this.currentSceneName;
      },
      goToSettings: () => {
        this.showSettingsScene(() => this.showTitleScene());
        return this.currentSceneName;
      }
    };
  }

  private readonly handleResize = (): void => {
    this.applyViewportLayout();
  };

  private readonly handleOrientationActionClick = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    void this.handleOrientationAction();
  };

  private async handleOrientationAction(): Promise<void> {
    this.setOrientationOverlayNote(ORIENTATION_PENDING_NOTE);
    if (this.orientationActionButton) {
      this.orientationActionButton.disabled = true;
      this.orientationActionButton.setAttribute('aria-busy', 'true');
    }

    const result = await this.tryLockLandscapeOrientation();
    window.setTimeout(this.handleResize, 150);

    if (this.orientationActionButton) {
      this.orientationActionButton.disabled = false;
      this.orientationActionButton.removeAttribute('aria-busy');
    }

    if (result === 'success') {
      this.setOrientationOverlayNote(ORIENTATION_SUCCESS_NOTE);
      return;
    }

    this.setOrientationOverlayNote(
      result === 'unsupported' ? ORIENTATION_UNSUPPORTED_NOTE : ORIENTATION_FAILED_NOTE
    );
  }

  private readonly handleTick = (ticker: Ticker): void => {
    this.sceneManager.update(ticker);
    this.updateDebugOverlay();
  };

  private readonly handleDebugOverlayKeyDown = (event: KeyboardEvent): void => {
    if (
      !import.meta.env.DEV ||
      event.key !== DEBUG_OVERLAY_TOGGLE_KEY ||
      event.repeat ||
      event.isComposing
    ) {
      return;
    }

    event.preventDefault();
    this.setDebugOverlayEnabled(!this.debugOverlayEnabled, { persist: true });
  };

  private readonly handleAudioUnlockPointerUp = (event: PointerEvent): void => {
    if (event.button !== POINTER_BUTTON_PRIMARY) {
      return;
    }

    this.audioManager.unlockFromUserGesture('pointer');
  };

  private readonly handleAudioUnlockKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat || event.isComposing || !AUDIO_UNLOCK_KEYS.has(event.key)) {
      return;
    }

    this.audioManager.unlockFromUserGesture('keyboard');
  };

  private applyViewportLayout(): void {
    const layout = calculateViewportLayout(this.config.baseResolution, {
      width: window.innerWidth,
      height: window.innerHeight
    });
    this.viewportLayout = layout;

    this.pixiApp.canvas.style.width = `${layout.displayWidth}px`;
    this.pixiApp.canvas.style.height = `${layout.displayHeight}px`;
    this.rootElement.style.setProperty('--game-display-width', `${layout.displayWidth}px`);
    this.rootElement.style.setProperty('--game-display-height', `${layout.displayHeight}px`);
    this.updateOrientationOverlay(layout);
    debugLogger.log('scene', 'viewport layout applied', summarizeLayout(layout));
    this.renderLayoutDebug(layout);
    this.debugOverlay.resize(layout);
    this.sceneManager.resize(layout);
  }

  private renderLayoutDebug(layout: ViewportLayout): void {
    this.layoutDebugLayer.removeChildren();
    if (!this.config.developmentTitle) {
      return;
    }

    const guide = new Graphics();
    guide
      .rect(layout.safeArea.x, layout.safeArea.y, layout.safeArea.width, layout.safeArea.height)
      .stroke({ color: 0xe1b84b, width: 2, alpha: 0.32 });
    this.layoutDebugLayer.addChild(guide);

    const layoutText = new Text({
      text: `viewport ${layout.viewportWidth}x${layout.viewportHeight} / display ${layout.displayWidth}x${layout.displayHeight} / scale ${layout.scale.toFixed(3)}`,
      style: {
        fill: 0xb9b3a7,
        fontFamily: 'system-ui, sans-serif',
        fontSize: 18
      }
    });
    layoutText.position.set(layout.safeArea.x, layout.safeArea.y + layout.safeArea.height - 30);
    this.layoutDebugLayer.addChild(layoutText);

    if (layout.shouldRecommendLandscape) {
      const orientationNotice = new Text({
        text: '横向き推奨',
        style: {
          fill: 0xe1b84b,
          fontFamily: 'system-ui, sans-serif',
          fontSize: 24,
          fontWeight: '700'
        }
      });
      orientationNotice.position.set(layout.safeArea.x, layout.safeArea.y);
      this.layoutDebugLayer.addChild(orientationNotice);
    }
  }

  private updateOrientationOverlay(layout: ViewportLayout): void {
    this.orientationOverlayVisible =
      !this.orientationOverlayDisabled && layout.shouldRecommendLandscape;
    this.rootElement.classList.toggle(
      'orientation-overlay-visible',
      this.orientationOverlayVisible
    );
    this.rootElement.classList.toggle(
      'orientation-overlay-disabled',
      this.orientationOverlayDisabled
    );
    this.orientationOverlay.setAttribute('aria-hidden', String(!this.orientationOverlayVisible));
    this.orientationOverlay.dataset.visible = String(this.orientationOverlayVisible);
    this.orientationOverlay.dataset.disabled = String(this.orientationOverlayDisabled);
    if (!this.orientationOverlayVisible) {
      this.setOrientationOverlayNote(ORIENTATION_DEFAULT_NOTE);
    }
  }

  private async tryLockLandscapeOrientation(): Promise<OrientationLockAttemptResult> {
    this.lastOrientationLockAttemptStatus = 'failed';

    try {
      if (this.getLayout().orientation === 'landscape') {
        this.lastOrientationLockAttemptStatus = 'success';
        return 'success';
      }

      const fullscreenResult = await requestGameFullscreen(this.rootElement);
      const lockResult = await lockScreenLandscape();
      if (lockResult === 'unsupported') {
        this.lastOrientationLockAttemptStatus =
          fullscreenResult === 'success' && window.innerWidth >= window.innerHeight
            ? 'success'
            : 'failed';
        return fullscreenResult === 'success' && window.innerWidth >= window.innerHeight
          ? 'success'
          : 'unsupported';
      }

      this.lastOrientationLockAttemptStatus = 'success';
      return 'success';
    } catch (error) {
      this.lastOrientationLockAttemptStatus = 'failed';
      debugLogger.log('scene', 'orientation lock failed', {
        reason: error instanceof Error ? error.message : String(error)
      });
      return 'failed';
    }
  }

  private setOrientationOverlayNote(message: string): void {
    if (this.orientationStatusText) {
      this.orientationStatusText.textContent = message;
    }
  }

  private getOrientationOverlayDebugState(): OrientationOverlayDebugState {
    const layout = this.getLayout();
    return {
      visible: this.orientationOverlayVisible,
      disabled: this.orientationOverlayDisabled,
      shouldRecommendLandscape: layout.shouldRecommendLandscape,
      lastLockAttemptStatus: this.lastOrientationLockAttemptStatus
    };
  }

  private showTitleScene(): void {
    this.currentNovelScene = null;
    this.setCurrentSceneName('title');
    void this.audioManager.stopBgm(0);
    this.sceneManager.changeScene(
      new TitleScene(this.createSceneServices(), this.config, {
        onStartNewGame: (slotId) => {
          this.activeSaveSlotId = slotId;
          void this.showNovelScene();
        },
        onLoadGame: (slotId) => {
          this.activeSaveSlotId = slotId;
          void this.showNovelScene();
        },
        onNavigate: (target) => {
          switch (target) {
            case 'caseList':
              this.showCaseListScene(() => this.showTitleScene());
              break;
            case 'gallery':
              this.showGalleryScene(() => this.showTitleScene());
              break;
            case 'settings':
              this.showSettingsScene(() => this.showTitleScene());
              break;
            case 'credits':
              this.showCreditsScene(() => this.showTitleScene());
              break;
          }
        }
      })
    );
  }

  private async showNovelScene(
    options: { saveProgressEnabled?: boolean; scenario?: ScenarioDocument } = {}
  ): Promise<void> {
    const scenario = options.scenario ?? (await this.loadScenarioForCurrentProgress());
    this.setCurrentSceneName('novel', {
      resumePoint: this.gameState.getResumePoint()
    });
    const saveProgressEnabled = options.saveProgressEnabled ?? true;
    const scene = new NovelScene(this.createSceneServices(), this.config, {
      onOpenEvidence: () =>
        this.showEvidenceScene(() => {
          void this.showNovelScene();
        }),
      onOpenCaseList: () =>
        this.showCaseListScene(() => {
          void this.showNovelScene();
        }),
      onOpenSettings: () =>
        this.showSettingsScene(() => {
          void this.showNovelScene();
        }),
      onGoToTitle: () => this.showTitleScene(),
      onCompleteCase: () => this.continueAfterCaseComplete(),
      scenario,
      onSaveProgress: saveProgressEnabled ? (mode) => this.saveActiveSlot(mode) : undefined
    });
    this.currentNovelScene = scene;
    this.sceneManager.changeScene(scene);
  }

  private showEvidenceScene(
    onBack: () => void = () => {
      void this.showNovelScene();
    }
  ): void {
    this.currentNovelScene = null;
    this.setCurrentSceneName('evidence');
    this.sceneManager.changeScene(
      new EvidenceScene(this.createSceneServices(), this.config, {
        onBack,
        onOpenCaseList: () => this.showCaseListScene(() => this.showEvidenceScene(onBack)),
        onOpenSettings: () => this.showSettingsScene(() => this.showEvidenceScene(onBack)),
        onGoToTitle: () => this.showTitleScene()
      })
    );
  }

  private showCaseListScene(onBack: () => void): void {
    this.currentNovelScene = null;
    this.setCurrentSceneName('caseList');
    this.sceneManager.changeScene(
      new CaseListScene(this.createSceneServices(), this.config, {
        onBack,
        onStartCase: (caseDefinition) => {
          void this.startCase(caseDefinition);
        },
        onOpenEvidence: () => this.showEvidenceScene(() => this.showCaseListScene(onBack)),
        onOpenSettings: () => this.showSettingsScene(() => this.showCaseListScene(onBack)),
        onGoToTitle: () => this.showTitleScene()
      })
    );
  }

  private showSettingsScene(onBack: () => void): void {
    this.currentNovelScene = null;
    this.setCurrentSceneName('settings');
    this.sceneManager.changeScene(
      new SettingsScene(this.createSceneServices(), this.config, {
        onBack,
        onOpenEvidence: () => this.showEvidenceScene(() => this.showSettingsScene(onBack)),
        onOpenCaseList: () => this.showCaseListScene(() => this.showSettingsScene(onBack)),
        onGoToTitle: () => this.showTitleScene()
      })
    );
  }

  private showCreditsScene(onBack: () => void): void {
    this.currentNovelScene = null;
    this.setCurrentSceneName('credits');
    this.sceneManager.changeScene(
      new CreditsScene(this.createSceneServices(), this.config, {
        onBack,
        onGoToTitle: () => this.showTitleScene()
      })
    );
  }

  private showGalleryScene(onBack: () => void): void {
    this.currentNovelScene = null;
    this.setCurrentSceneName('gallery');
    this.sceneManager.changeScene(
      new GalleryScene(this.createSceneServices(), {
        onBack,
        onGoToTitle: () => this.showTitleScene()
      })
    );
  }

  private showEndingScene(showGalleryUnlockNotice = false): void {
    this.currentNovelScene = null;
    this.setCurrentSceneName('ending');
    this.sceneManager.changeScene(
      new EndingScene(this.createSceneServices(), {
        showGalleryUnlockNotice,
        onComplete: () => this.showTitleScene()
      })
    );
  }

  private createSceneServices(): SceneServices {
    return {
      getLayout: () => this.getLayout(),
      getState: () => this.gameState,
      getInput: () => this.inputManager,
      getAssets: () => this.assetLoader,
      getSaveManager: () => this.saveManager,
      getAudio: () => this.audioManager
    };
  }

  private async loadScenarioForCurrentProgress(): Promise<ScenarioDocument> {
    const progress = this.gameState.getProgress();
    try {
      return await loadScenarioForResume(progress.currentCaseId, progress.resumePoint.scenarioId);
    } catch (error) {
      console.warn(
        '[GameApp] Failed to load current scenario. Falling back to initial case.',
        error
      );
      const fallback = await loadScenarioForCase(this.config.initialCaseId);
      this.gameState.startScenario(fallback.id, fallback.caseId, fallback.startNodeId);
      return fallback;
    }
  }

  private async startCase(caseDefinition: CaseDefinition): Promise<void> {
    if (!this.gameState.isCaseUnlocked(caseDefinition.id)) {
      debugLogger.log('scenario', 'start case blocked by locked status', {
        caseId: caseDefinition.id
      });
      return;
    }

    const scenario = await loadScenarioForCase(caseDefinition.id);
    this.gameState.startScenario(scenario.id, scenario.caseId, scenario.startNodeId);
    this.saveActiveSlot('manual');
    await this.showNovelScene({ scenario });
  }

  private async continueAfterCaseComplete(): Promise<void> {
    const completedCaseId = this.gameState.getProgress().currentCaseId;
    const nextCase = getNextCaseDefinition(completedCaseId);
    if (!nextCase) {
      debugLogger.log('scenario', 'case complete with no next case', { completedCaseId });
      if (completedCaseId === 'case10') {
        this.saveActiveSlot('auto');
        this.showEndingScene(true);
        return;
      }
      this.showTitleScene();
      return;
    }

    try {
      const scenario = await loadScenarioForCase(nextCase.id);
      this.gameState.startScenario(scenario.id, scenario.caseId, scenario.startNodeId);
      this.saveActiveSlot('auto');
      debugLogger.log('scenario', 'continue to next case', {
        completedCaseId,
        nextCaseId: nextCase.id
      });
      await this.showNovelScene({ scenario });
    } catch (error) {
      console.warn('[GameApp] Failed to continue to next case. Falling back to title.', error);
      debugLogger.log('scenario', 'continue to next case failed', {
        completedCaseId,
        nextCaseId: nextCase.id,
        reason: error instanceof Error ? error.message : String(error)
      });
      this.showTitleScene();
    }
  }

  private setCurrentSceneName(
    nextSceneName: E2ESceneName,
    details?: Record<string, unknown>
  ): void {
    const previousSceneName = this.currentSceneName;
    this.currentSceneName = nextSceneName;
    debugLogger.log('scene', 'change scene', {
      from: previousSceneName,
      to: nextSceneName,
      ...details
    });
    this.updateDebugOverlay();
  }

  private saveActiveSlot(mode: 'auto' | 'manual'): NovelSaveProgressResult {
    if (this.activeSaveSlotId === null) {
      debugLogger.log('save', 'save active slot skipped', { mode, reason: 'noActiveSlot' });
      return {
        status: 'skipped',
        reason: 'noActiveSlot'
      };
    }

    try {
      const snapshot = this.gameState.getSnapshot();
      const record = this.saveManager.saveSlot(
        this.activeSaveSlotId,
        snapshot,
        getCaseSaveTitle(snapshot.progress.currentCaseId)
      );
      debugLogger.log('save', 'save active slot', {
        mode,
        slotId: record.slotId,
        savedAt: record.savedAt
      });
      return {
        status: 'saved',
        slotId: record.slotId,
        savedAt: record.savedAt
      };
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      console.warn('[GameApp] Failed to save active slot.', error);
      debugLogger.log('save', 'save active slot failed', {
        mode,
        slotId: this.activeSaveSlotId,
        reason
      });
      return {
        status: 'failed',
        reason
      };
    }
  }

  private setDebugOverlayEnabled(
    enabled: boolean,
    options: { persist?: boolean } = {}
  ): DebugOverlayState {
    const nextEnabled = import.meta.env.DEV ? enabled : false;
    this.debugOverlayEnabled = nextEnabled;
    this.debugOverlay.setEnabled(nextEnabled);
    this.updateDebugOverlay();
    if (import.meta.env.DEV && options.persist) {
      writeDebugOverlayStorageValue(nextEnabled);
    }
    debugLogger.log('scene', 'set debug overlay enabled', {
      enabled: nextEnabled,
      requested: enabled,
      persist: options.persist
    });
    return this.getDebugOverlayState();
  }

  private getDebugOverlayState(): DebugOverlayState {
    return this.debugOverlay.getState();
  }

  private getRuntimeDebugState(): RuntimeDebugState {
    const currentScene = this.sceneManager.getCurrentScene();
    return {
      sceneName: this.currentSceneName,
      currentSceneType: currentScene?.constructor.name ?? null,
      stageChildCount: this.pixiApp.stage.children.length,
      sceneRootChildCount: this.sceneRoot.children.length,
      layoutDebugLayerChildCount: this.layoutDebugLayer.children.length,
      debugOverlayChildCount: this.debugOverlay.children.length,
      currentSceneChildCount: currentScene?.container.children.length ?? 0,
      tickerListenerCount: this.pixiApp.ticker.count,
      input: this.inputManager.getDebugState(),
      audio: this.audioManager.getDebugState(),
      novel: this.currentNovelScene?.getInputDebugState() ?? null
    };
  }

  private updateDebugOverlay(): void {
    this.debugOverlay.setSnapshot(this.createDebugOverlaySnapshot());
  }

  private createDebugOverlaySnapshot(): DebugOverlaySnapshot {
    const snapshot = this.gameState.getSnapshot();
    const resumePoint = snapshot.progress.resumePoint;
    const audio = this.audioManager.getDebugState();
    const input = this.inputManager.getDebugState();
    const novelInput = this.currentNovelScene?.getInputDebugState() ?? null;
    return {
      sceneName: this.currentSceneName,
      currentScenarioId: snapshot.progress.currentScenarioId,
      currentCaseId: snapshot.progress.currentCaseId,
      nodeId: resumePoint.nodeId,
      lineId: resumePoint.lineId ?? null,
      lineIndex: resumePoint.lineIndex,
      currentBgmId: audio.currentBgmId,
      currentBgmPlaceholder: audio.currentBgmPlaceholder,
      backgroundId: novelInput?.background.currentBackgroundId ?? null,
      backgroundEffect: novelInput?.background.currentEffect ?? 'none',
      backgroundPlaceholder: novelInput?.background.placeholder ?? false,
      inputLocked: input.locked,
      inputLockReasons: input.lockReasons,
      novelBlocked: novelInput?.blocked ?? false,
      novelBlockReasons: novelInput?.reasons ?? []
    };
  }

  private async e2eStartNewGame(slotId: number) {
    debugLogger.log('scenario', 'e2e start new game', { slotId });
    this.activeSaveSlotId = slotId;
    this.gameState.resetProgress(this.config);
    this.gameState.setCurrentCase(this.config.initialCaseId, 'investigating');
    const record = this.saveManager.saveSlot(
      slotId,
      this.gameState.getSnapshot(),
      getCaseSaveTitle(this.config.initialCaseId)
    );
    await this.showNovelScene();
    return record;
  }

  private async e2eLoadSlot(slotId: number) {
    debugLogger.log('scenario', 'e2e load slot request', { slotId });
    const result = this.saveManager.loadSlot(slotId);
    if (result.status === 'valid' && result.record) {
      this.activeSaveSlotId = slotId;
      this.gameState.hydrate(result.record.snapshot);
      await this.showNovelScene();
    }
    return result;
  }

  private e2eUpdateSettings(settings: Partial<GameSettings>): GameSettings {
    debugLogger.log('save', 'e2e update settings', { settings });
    if (settings.masterVolume !== undefined) {
      this.audioManager.setMasterVolume(settings.masterVolume);
    }
    if (settings.bgmVolume !== undefined) {
      this.audioManager.setBgmVolume(settings.bgmVolume);
    }
    if (settings.seVolume !== undefined) {
      this.audioManager.setSeVolume(settings.seVolume);
    }
    if (settings.voiceVolume !== undefined) {
      this.audioManager.setVoiceVolume(settings.voiceVolume);
    }

    this.gameState.updateSettings(settings);
    this.saveManager.saveSettings(this.gameState.getSettings());
    return this.gameState.getSettings();
  }

  private async e2eGoToScenarioNode(
    nodeId: string,
    lineIndex: number,
    caseId?: string
  ): Promise<E2ESceneName> {
    const targetCaseId =
      caseId ?? inferCaseIdFromNodeId(nodeId, this.gameState.getProgress().currentCaseId);
    debugLogger.log('scenario', 'e2e go to scenario node', {
      nodeId,
      lineIndex,
      caseId: targetCaseId
    });
    const scenario = await loadScenarioForCase(targetCaseId);
    this.gameState.setCurrentCase(targetCaseId, 'investigating');
    this.gameState.setResumePoint({
      scenarioId: scenario.id,
      nodeId,
      lineIndex: Number.isFinite(lineIndex) ? Math.max(0, Math.floor(lineIndex)) : 0
    });
    await this.showNovelScene({ saveProgressEnabled: false, scenario });
    return this.currentSceneName;
  }

  private async e2ePlayCase0ToEnd(choiceId = 'case0_choice_town'): Promise<E2EPlaythroughResult> {
    return this.e2ePlayCaseToEnd(this.config.initialCaseId, choiceId);
  }

  private async e2ePlayCaseToEnd(caseId: string, choiceId?: string): Promise<E2EPlaythroughResult> {
    const skipBgmPlayback = shouldSkipE2EBgmPlayback();
    debugLogger.log('scenario', 'e2e play case to end', { caseId, choiceId, skipBgmPlayback });
    const scenario = await loadScenarioForCase(caseId);
    this.gameState.startScenario(scenario.id, scenario.caseId, scenario.startNodeId);
    const runner = new ScenarioRunner(scenario);
    const visitedNodeIds: string[] = [];
    const bgmTimeline: E2EBgmTimelineEntry[] = [];
    let chosenChoiceId = choiceId ?? '';

    for (let step = 0; step < 100; step += 1) {
      const node = runner.getCurrentNode();
      visitedNodeIds.push(node.id);
      debugLogger.log('scenario', 'e2e node', {
        step,
        nodeId: node.id,
        lineCount: node.lines.length,
        choiceCount: node.choices?.length ?? 0,
        bgm: node.bgm
      });
      if (!skipBgmPlayback) {
        await this.playE2ENodeBgm(node);
      }
      bgmTimeline.push(this.createE2EBgmTimelineEntry(node));
      this.recordE2ELines(runner);
      await this.executeE2ENodeActions(runner);

      const choices = runner.getChoices();
      if (choices.length > 0) {
        const choice =
          choices.find((candidate) => candidate.id === choiceId) ??
          choices.find((candidate) => candidate.id?.includes('choice_town')) ??
          choices[0];
        chosenChoiceId = choice.id ?? choice.text;
        debugLogger.log('scenario', 'e2e choose choice', {
          nodeId: node.id,
          choiceId: choice.id ?? null,
          next: choice.next
        });
        await this.executeE2EActions(choice.actions, `choice ${choice.id ?? choice.text}`);
        runner.chooseChoice(choice);
        continue;
      }

      if (!runner.moveToNextNode()) {
        return {
          chosenChoiceId,
          visitedNodeIds,
          bgmTimeline,
          finalScene: this.currentSceneName,
          snapshot: this.gameState.getSnapshot()
        };
      }
    }

    throw new Error(
      `E2E ${caseId} playthrough exceeded step limit for choice "${choiceId ?? 'first choice'}".`
    );
  }

  private createE2EBgmTimelineEntry(node: ScenarioNode): E2EBgmTimelineEntry {
    const audio = this.audioManager.getDebugState();
    return {
      nodeId: node.id,
      requestedBgm: node.bgm === undefined ? 'unchanged' : node.bgm,
      currentBgmId: audio.currentBgmId,
      activeBgmHowlCount: audio.activeBgmHowlCount,
      bgmRequestCount: audio.bgmRequestCount,
      bgmStartCount: audio.bgmStartCount,
      lastBgmRequestId: audio.lastBgmRequestId,
      lastBgmStartedId: audio.lastBgmStartedId
    };
  }

  private recordE2ELines(runner: ScenarioRunner): void {
    this.recordE2ELine(runner.getCurrentLine(), runner);
    while (runner.canAdvanceLine()) {
      runner.advanceLine();
      this.recordE2ELine(runner.getCurrentLine(), runner);
    }
  }

  private recordE2ELine(line: ScenarioLine, runner: ScenarioRunner): void {
    const position = runner.getCurrentPosition();
    this.gameState.setResumePoint(position);
    if (!this.gameState.hasReadLine(position)) {
      this.gameState.appendBacklogLine(position, line);
      this.gameState.markLineRead(position);
    }
  }

  private async executeE2ENodeActions(runner: ScenarioRunner): Promise<void> {
    await this.executeE2EActions(runner.consumeNodeActions(), `node ${runner.getState().nodeId}`);
  }

  private async executeE2EActions(
    actions: readonly unknown[] | undefined,
    contextLabel: string
  ): Promise<void> {
    if (!actions?.length) {
      return;
    }

    const context: ScenarioActionExecutionContext = {
      gameState: this.gameState,
      audio: {
        playSe: async (id) => ({
          id,
          channel: 'se',
          played: false,
          placeholder: true
        })
      },
      onScreenShake: () => undefined,
      onFade: () => undefined,
      onGoToTitle: () => this.continueAfterCaseComplete(),
      warn: (warning) => {
        console.warn(`[E2E] ${warning.reason}`, warning.action);
      }
    };

    for (const action of actions) {
      const result = await executeScenarioAction(action, context);
      if (!result.executed) {
        throw new Error(
          `E2E scenario action failed at ${contextLabel}: ${result.warning?.reason ?? result.type}`
        );
      }
    }
  }

  private async playE2ENodeBgm(node: ScenarioNode): Promise<void> {
    if (node.bgm === undefined) {
      return;
    }

    if (node.bgm === null) {
      await this.audioManager.stopBgm();
      return;
    }

    await this.audioManager.playBgm(node.bgm, { fadeMs: 500, loop: true });
  }
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function shouldEnableDebugOverlay(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return false;
  }

  const queryValue = new URLSearchParams(window.location.search).get(DEBUG_OVERLAY_QUERY_PARAM);
  if (queryValue !== null) {
    return isEnabledFlagValue(queryValue);
  }

  try {
    return isEnabledFlagValue(window.localStorage.getItem(DEBUG_OVERLAY_STORAGE_KEY));
  } catch {
    return false;
  }
}

function shouldDisableOrientationOverlay(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  return isEnabledFlagValue(
    new URLSearchParams(window.location.search).get(DISABLE_ORIENTATION_OVERLAY_QUERY_PARAM)
  );
}

function shouldSkipE2EBgmPlayback(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return isEnabledFlagValue(
    new URLSearchParams(window.location.search).get(FAST_E2E_AUDIO_SKIP_QUERY_PARAM)
  );
}

function createOrientationOverlayElement(): HTMLElement {
  const overlay = document.createElement('section');
  overlay.className = 'orientation-overlay';
  overlay.setAttribute('aria-label', 'スマホ横画面推奨');
  overlay.setAttribute('aria-hidden', 'true');

  const rainLayer = document.createElement('div');
  rainLayer.className = 'orientation-overlay__rain';
  rainLayer.setAttribute('aria-hidden', 'true');

  const panel = document.createElement('div');
  panel.className = 'orientation-overlay__panel';

  const kicker = document.createElement('p');
  kicker.className = 'orientation-overlay__kicker';
  kicker.textContent = 'Mobile Investigation';

  const title = document.createElement('h2');
  title.className = 'orientation-overlay__title';
  title.textContent = '横向きで捜査開始';

  const body = document.createElement('p');
  body.className = 'orientation-overlay__body';
  body.textContent = '端末を横にすると、事件資料、立ち絵、セリフ枠を大きく表示できます。';

  const action = document.createElement('button');
  action.className = 'orientation-overlay__button';
  action.type = 'button';
  action.dataset.orientationAction = '1';
  action.textContent = '横画面に切り替える';

  const note = document.createElement('p');
  note.className = 'orientation-overlay__note';
  note.dataset.orientationStatus = '1';
  note.textContent = ORIENTATION_DEFAULT_NOTE;

  panel.append(kicker, title, body, action, note);
  overlay.append(rainLayer, panel);
  return overlay;
}

type OrientationLockAttemptResult = 'success' | 'unsupported' | 'failed';
type FullscreenAttemptResult = 'success' | 'unsupported';

async function requestGameFullscreen(rootElement: HTMLElement): Promise<FullscreenAttemptResult> {
  if (document.fullscreenElement) {
    return 'success';
  }

  const fullscreenTarget = rootElement.ownerDocument.documentElement;
  const requestFullscreen =
    fullscreenTarget.requestFullscreen?.bind(fullscreenTarget) ??
    (
      fullscreenTarget as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void> | void;
      }
    ).webkitRequestFullscreen?.bind(fullscreenTarget);

  if (!requestFullscreen) {
    return 'unsupported';
  }

  await Promise.resolve(requestFullscreen());
  return 'success';
}

async function lockScreenLandscape(): Promise<OrientationLockAttemptResult> {
  const modernOrientation = screen.orientation as
    | (ScreenOrientation & {
        lock?: (orientation: string) => Promise<void>;
      })
    | undefined;
  if (modernOrientation?.lock) {
    await modernOrientation.lock('landscape');
    return 'success';
  }

  const legacyScreen = screen as Screen & {
    lockOrientation?: (orientation: string) => boolean;
    mozLockOrientation?: (orientation: string) => boolean;
    msLockOrientation?: (orientation: string) => boolean;
  };
  const legacyLock =
    legacyScreen.lockOrientation ??
    legacyScreen.mozLockOrientation ??
    legacyScreen.msLockOrientation;
  if (!legacyLock) {
    return 'unsupported';
  }

  return legacyLock.call(screen, 'landscape') ? 'success' : 'failed';
}

function writeDebugOverlayStorageValue(enabled: boolean): void {
  try {
    window.localStorage.setItem(DEBUG_OVERLAY_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage failures; the keyboard toggle should still affect the current session.
  }
}

function isEnabledFlagValue(value: string | null): boolean {
  if (value === null) {
    return false;
  }

  return ['1', 'true', 'on', 'yes'].includes(value.trim().toLowerCase());
}

function summarizeLayout(layout: ViewportLayout): Record<string, unknown> {
  return {
    viewportWidth: layout.viewportWidth,
    viewportHeight: layout.viewportHeight,
    displayWidth: layout.displayWidth,
    displayHeight: layout.displayHeight,
    scale: layout.scale,
    shouldRecommendLandscape: layout.shouldRecommendLandscape,
    safeArea: layout.safeArea
  };
}

function inferCaseIdFromNodeId(nodeId: string, fallbackCaseId: string): string {
  const match = /^case\d+/.exec(nodeId);
  return match?.[0] ?? fallbackCaseId;
}

function cloneViewportLayout(layout: ViewportLayout): ViewportLayout {
  return {
    ...layout,
    safeArea: { ...layout.safeArea }
  };
}
